import { VisibilityElement } from '@/components/custom-elements/visibility'

/**
 * MODAL CUSTOM ELEMENT
 * Handled as a custom element due to their being next to no state to manage, and due to how neatly it interacts with Tailwind
 *
 * For set up:
 *   1. Ensure you wrap your modal elements with <x-modal>.
 *   2. Pass a 'data-id' if you want intend to nest modals.
 *   3. Provide any buttons you want to modify modal state as a child, using "data-modal-role" with open, close, or toggle.
 *   4. To show or hide elements based on modal state, set a tailwind group and use "group-data-visible:" for stateful styling.
 *   5. Optional: Add 'data-hide-on-blur="true"' to hide the modal when it loses focus.
 *   6. Optional: Add 'data-toggle-group="true"' to automatically close other modals with the same attribute when this modal opens.
 *   7. Optional: Add 'data-close-all' so that when this modal is clicked, all descendant x-modals with data-ui="visible" are closed.
 *
 */

export class ModalElement extends VisibilityElement {
  // Child role selectors
  static OPEN_SELECTOR = '[data-modal-role~="open"]'
  static CLOSE_SELECTOR = '[data-modal-role~="close"]'
  static TOGGLE_SELECTOR = '[data-modal-role~="toggle"]'
  static DISABLE_SELECTOR = '[data-modal-role~="disable"]'

  openElement: NodeListOf<Element> | undefined
  closeElement: NodeListOf<Element> | undefined
  toggleElement: NodeListOf<Element> | undefined
  disableElement: NodeListOf<Element> | undefined
  hideOnBlur: boolean = false
  toggleGroup: boolean = false
  closeAllOnClick: boolean = false

  constructor() {
    super()
    const originalHandleShow = this.handleShow
    this.handleShow = () => {
      originalHandleShow.call(this)
      if (this.dataset.toggleGroup === 'true') {
        this.closeOtherModalsInGroup()
      }
    }
  }

  parseIdSelector() {
    const id = this.dataset.id
    return id ? `[data-modal-parent~="${id}"]` : ''
  }

  fetchChildren() {
    const idSelector = this.parseIdSelector()
    const targetWrapper = idSelector ? document : this
    this.openElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.OPEN_SELECTOR}`)
    this.closeElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.CLOSE_SELECTOR}`)
    this.toggleElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.TOGGLE_SELECTOR}`)
    this.disableElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.DISABLE_SELECTOR}`)
  }

  handleBlur = (event: FocusEvent) => {
    // Only run if hideOnBlur is enabled
    if (!this.hideOnBlur) return

    // Check if the related target (that received focus) is inside this element
    // If not, hide the modal
    const currentTarget = event.currentTarget as Node
    const relatedTarget = event.relatedTarget as Node

    if (relatedTarget && !currentTarget.contains(relatedTarget)) {
      this.handleHide()
    }
  }

  closeAllDescendantModals() {
    const visibleDescendants = this.querySelectorAll('x-modal[data-ui="visible"]') as NodeListOf<ModalElement>
    visibleDescendants.forEach((modal) => modal.handleHide())
  }

  handleCloseAllClick = (event: MouseEvent) => {
    const target = event.target as Node
    const descendantModals = this.querySelectorAll('x-modal')
    const clickedInsideDescendant = Array.from(descendantModals).some(
      (modal) => modal !== this && modal.contains(target)
    )
    if (!clickedInsideDescendant) {
      this.closeAllDescendantModals()
    }
  }

  closeOtherModalsInGroup() {
    // Find the parent container (or use document if no specific parent)
    const parentContainer = this.parentElement || document

    // Find all other modals with toggleGroup enabled in the same parent
    const otherModals = parentContainer.querySelectorAll(
      'x-modal[data-toggle-group="true"]'
    ) as NodeListOf<ModalElement>

    // Close all other modals in the group (excluding this one)
    otherModals.forEach((modal) => {
      if (modal !== this && modal.dataset.ui === 'visible') {
        modal.handleHide()
      }
    })
  }

  parseConfig() {
    // Parse hideOnBlur from the data attribute
    this.hideOnBlur = this.dataset.hideOnBlur === 'true'
    // Parse toggleGroup from the data attribute
    this.toggleGroup = this.dataset.toggleGroup === 'true'
    this.closeAllOnClick = this.hasAttribute('data-close-all')
  }

  addEventListeners() {
    super.addEventListeners()
    if (this.closeAllOnClick) {
      this.addEventListener('click', this.handleCloseAllClick)
    }
    this.openElement?.forEach((el) => el?.addEventListener('click', this.handleShow))
    this.closeElement?.forEach((el) => el?.addEventListener('click', this.handleHide))
    this.toggleElement?.forEach((el) => el?.addEventListener('click', this.handleToggle))
    this.disableElement?.forEach((el) => el?.addEventListener('click', this.handleDisable))

    // Add blur event listener if hideOnBlur is enabled
    if (this.hideOnBlur) {
      this.addEventListener('focusout', this.handleBlur)
    }
  }

  removeEventListeners() {
    super.removeEventListeners()
    if (this.closeAllOnClick) {
      this.removeEventListener('click', this.handleCloseAllClick)
    }
    this.openElement?.forEach((el) => el?.removeEventListener('click', this.handleShow))
    this.closeElement?.forEach((el) => el?.removeEventListener('click', this.handleHide))
    this.toggleElement?.forEach((el) => el?.removeEventListener('click', this.handleToggle))
    this.disableElement?.forEach((el) => el?.removeEventListener('click', this.handleDisable))

    // Remove blur event listener if it was added
    if (this.hideOnBlur) {
      this.removeEventListener('focusout', this.handleBlur)
    }
  }

  // Expose the event handlers for external buttons
  getListeners() {
    return {
      open: this.handleShow,
      close: this.handleHide,
      toggle: this.handleToggle,
      disable: this.handleDisable
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.parseConfig()
    this.fetchChildren()
    this.addEventListeners()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }
}
